import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

/**
 * Wraps a trigger element (button/icon) and shows a preview dialog
 * with editable WhatsApp message before opening wa.me.
 *
 * Props:
 *  - rawPhone: phone string (any format, will be normalized)
 *  - defaultMessage: precompiled WA message
 *  - title: dialog title (default "Anteprima messaggio WhatsApp")
 *  - children: trigger element rendered as-is (will receive onClick)
 *  - testid: optional test id for the trigger wrapper
 */
function normalize(rawPhone) {
  let n = String(rawPhone || "").replace(/[^\d+]/g, "");
  if (!n) return null;
  if (!n.startsWith("+")) {
    if (n.startsWith("00")) n = "+" + n.slice(2);
    else if (n.startsWith("39") && n.length >= 11) n = "+" + n;
    else n = "+39" + n;
  }
  return n;
}

export function WhatsAppButton({ rawPhone, defaultMessage = "", title = "💬 Anteprima messaggio WhatsApp", children, testid }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState(defaultMessage);

  const normalized = normalize(rawPhone);
  if (!normalized) return null;
  const phone = normalized.replace(/\D/g, "");

  const openDialog = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setMessage(defaultMessage);
    setOpen(true);
  };

  const confirm = () => {
    const text = (message || "").trim();
    const url = `https://wa.me/${phone}${text ? `?text=${encodeURIComponent(text)}` : ""}`;
    window.open(url, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  return (
    <>
      <span data-testid={testid} onClick={openDialog} className="inline-flex">
        {children}
      </span>
      <Dialog open={open} onOpenChange={(o) => { if (!o) setOpen(false); }}>
        <DialogContent className="max-w-lg" data-testid="whatsapp-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-green-700">{title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-green-50 border border-green-200 rounded p-2 text-[11px] text-green-800">
              Destinatario: <span className="font-mono font-semibold">{normalized}</span>
            </div>
            <div>
              <Label className="text-xs">Messaggio</Label>
              <Textarea rows={8} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="wa-message" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="wa-confirm-open" onClick={confirm} className="bg-green-600 hover:bg-green-700 text-white">
              💬 Apri WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default WhatsAppButton;
